import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Form, message, Select, Row, Col } from 'antd';
import Button from 'components/generalUI/Button';
import useSendEmailMutation from 'apollo/resolvers/contactUs/sendEmail';
import emailRegExp from 'utils/validations/emailRegExp';
import { DefaultOptionType } from 'antd/lib/select';
import styles from './HelpForm.module.scss';

interface HelpFormProps {
  className?: string;
}

const THEME_OPTIONS = [
  { key: 'educationalVisit', value: 'Visita educativa-pedagógica' },
  { key: 'tourOperators', value: 'Operadores turísticos' },
  { key: 'complaints', value: 'Sugerencias, quejas y reclamos' },
  { key: 'institutionalEvents', value: 'Eventos institucionales' },
  { key: 'other', value: 'Otros' },
];

const HelpForm: FC<HelpFormProps> = ({ className }) => {
  const { t } = useTranslation();

  // `value` is what gets emailed to the backend, so it stays the literal
  // Spanish string. Only `label` is translated.
  // TODO: switch `value` to a stable code once the backend accepts one.
  const themes: DefaultOptionType[] = THEME_OPTIONS.map(({ key, value }) => ({
    label: t(`pages.contact.helpForm.themes.${key}`),
    value,
  }));

  const [form] = Form.useForm();
  const [useSendEmail] = useSendEmailMutation();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [selectedTheme, setSelectedTheme] = useState<string>(themes[0].value as string);

  const onSubmit = async () => {
    setDisabled(true);
    await form.validateFields();
    const values = form.getFieldsValue() as {
      email: string;
      name: string;
      message: string;
    };
    const response = await useSendEmail({
      variables: {
        input: {
          email: values.email,
          name: values.name,
          message: values.message,
          theme: selectedTheme,
        },
      },
    });
    if (response) {
      await message.success(t('navigation.footer.messageSent'));
    } else {
      await message.error(t('navigation.footer.messageSentError'));
    }
    form.resetFields();
    setDisabled(false);
  };

  return (
    <div className={[styles.Form, className].filter(Boolean).join(' ')}>
      <Form form={form} layout="vertical">
        <h1 className={styles.FormTitle}>{t('navigation.footer.helpFormTitle')}</h1>
        <p className={styles.FormAttentionSchedule}>
          {t('navigation.footer.helpFormAttentionSchedule')}
        </p>
        <Row justify="space-between">
          <Col xs={24} md={11}>
            <p className={styles.formLabel}>{t('navigation.footer.helpFormNameField')}</p>
            <Form.Item
              name="name"
              rules={[{ required: true, message: t('navigation.footer.requiredName') }]}
              className={styles.FormItem}
            >
              <Input className={styles.Input} />
            </Form.Item>
          </Col>
          <Col xs={24} md={11}>
            <p className={styles.formLabel}>
              {t('navigation.footer.helpFormEmailField')}
            </p>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: t('navigation.footer.requiredEmail') },
                {
                  message: t('navigation.footer.invalidEmail'),
                  pattern: emailRegExp(),
                },
              ]}
              className={styles.FormItem}
            >
              <Input className={styles.Input} />
            </Form.Item>
          </Col>
        </Row>
        <p className={styles.formLabel}>{t('navigation.footer.themeFormMessageField')}</p>
        <Form.Item
          name="theme"
          rules={[{ required: true, message: t('navigation.footer.requiredMessage') }]}
          className={styles.FormItem}
        >
          <Select
            options={themes}
            placeholder={t('pages.contact.helpForm.selectReasonPlaceholder')}
            onChange={setSelectedTheme}
          />
        </Form.Item>
        <p className={styles.formLabel}>{t('navigation.footer.helpFormMessageField')}</p>
        <Form.Item
          name="message"
          rules={[{ required: true, message: t('navigation.footer.requiredMessage') }]}
          className={styles.FormItem}
        >
          <Input.TextArea className={styles.Input} />
        </Form.Item>
        <Form.Item className={styles.FormItem}>
          <Button
            className={styles.FormButton}
            shape="round"
            onClick={onSubmit}
            disabled={disabled}
          >
            {t('navigation.footer.helpFormButton')}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

HelpForm.defaultProps = {
  className: undefined,
};

export default HelpForm;
